// Pincode serviceability for the revamp PDP hero, through GoKwik's KwikShip
// EDD API. GoKwik own the wording: `text` comes back from KwikShip ("Delivery
// by 10th Oct", "No Delivery Available") and is shown as-is, so copy changes
// need no theme release.
//
// Response facts this relies on, all checked against the live API:
// - Every answered lookup is HTTP 201 with `status: 200`; serviceability is
//   only in the entry: a real `edd` and no `error` means deliverable.
// - The API does no input validation (`5601`, `abcdef` both answer "No
//   Delivery Available"), so the six-digit check here is the only one.
// - A bad or missing token is the only HTTP error (401).
//
// City chips carrying a pincode run the same lookup as typing. Their answers
// are shown but never saved: a chip pincode is a preview of that city, not
// the shopper's address, so it must not reach checkout's prefill.
(() => {
  const EDD_ENDPOINT = 'https://api.gokwik.co/kwikship/v2/pincode/edd';
  const EDD_TIMEOUT_MS = 8000;
  const EVT_PREFIX = 'hira-product-form:';

  // GoKwik checkout reads this key to prefill the pincode (confirmed by
  // GoKwik). It holds the bare six digits, as their own widget wrote it.
  const PINCODE_KEY = 'kwik-edd-pincode';
  const PINCODE_PATTERN = /^[1-9]\d{5}$/;

  const STATES = ['is-loading', 'is-available', 'is-unavailable'];

  // Module scope, so answers survive `shopify:section:load` re-renders and a
  // size switched back and forth never repeats a call. Promises are cached,
  // not results, so two quick clicks share one request. Failures are evicted
  // so a retry really retries.
  const lookups = new Map();

  // Private browsing and blocked site data throw here rather than return null.
  const readStored = (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const writeStored = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage unavailable: checkout simply won't prefill.
    }
  };

  const requestEdd = ({ token, merchantId, pincode, sku, price }) => {
    const key = `${pincode}:${sku}`;
    if (lookups.has(key)) return lookups.get(key);

    const request = fetch(EDD_ENDPOINT, {
      method: 'POST',
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pincode,
        type: 'pdp',
        // GoKwik want the product price in rupees; Shopify stores paise. This is
        // an API payload, not a displayed price.
        cart_total: price / 100,
        shopify_session_id: null,
        m_id: merchantId,
        items: [{ quantity: 1, sku }],
      }),
      signal: AbortSignal.timeout?.(EDD_TIMEOUT_MS),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`GoKwik EDD responded ${response.status}`);
        return response.json();
      })
      .then(({ data }) => {
        // `data` is an array with one entry per item; an empty `items` list
        // returns a bare object instead.
        const entry = Array.isArray(data) ? data[0] : data;
        if (!entry?.text) throw new Error('GoKwik EDD returned no estimate');
        return { available: Boolean(entry.edd) && !entry.error, text: entry.text };
      });

    request.catch(() => lookups.delete(key));
    lookups.set(key, request);
    return request;
  };

  class HiraPincode extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-hira-pincode]');
      this.button = this.querySelector('[data-hira-pincode-check]');
      this.note = this.querySelector('[data-hira-delivery-note]');
      this.noteText = this.querySelector('[data-hira-delivery-note-text]');
      if (!this.input || !this.button || !this.note || !this.noteText) return;

      const { checkLabel, changeLabel, checkingLabel, failedText, token, merchantId, sku, price } = this.dataset;
      this.labels = { check: checkLabel, change: changeLabel, checking: checkingLabel };
      this.failedText = failedText;
      this.credentials = { token, merchantId };
      this.variant = { sku: sku ?? '', price: Number(price) || 0 };

      // The merchant's Delivery Note is richtext, so its nodes are kept rather
      // than its text — restoring it must bring the markup back intact.
      this.idleNodes = [...this.noteText.childNodes].map((node) => node.cloneNode(true));
      this.idleHidden = this.note.hidden;

      this.cities = [...this.querySelectorAll('[data-hira-city-pincode]')];

      // The one record of what the checker is showing, read by every handler:
      // 'idle' (the merchant's note), 'loading', 'answered' (GoKwik replied,
      // deliverable or not) or 'failed' (GoKwik could not be reached).
      this.status = 'idle';
      this.requestId = 0;
      // The pincode of the chip that filled the input; null when it was typed.
      this.cityPincode = null;

      this.handleInput = this.handleInput.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleVariantChanged = this.handleVariantChanged.bind(this);
      this.handleCityClick = this.handleCityClick.bind(this);

      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('keydown', this.handleKeydown);
      this.button.addEventListener('click', this.handleClick);
      this.addEventListener('click', this.handleCityClick);

      // The controller emits on its <hira-product-form> ancestor, not on document.
      this.host = this.closest('hira-product-form');
      this.host?.addEventListener(`${EVT_PREFIX}variant-changed`, this.handleVariantChanged);

      // A returning visitor sees their estimate without retyping.
      const stored = readStored(PINCODE_KEY);
      if (this.isConfigured && PINCODE_PATTERN.test(stored ?? '')) {
        this.input.value = stored;
        this.check();
      } else {
        this.syncButton();
      }
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this.handleInput);
      this.input?.removeEventListener('keydown', this.handleKeydown);
      this.button?.removeEventListener('click', this.handleClick);
      this.removeEventListener('click', this.handleCityClick);
      this.host?.removeEventListener(`${EVT_PREFIX}variant-changed`, this.handleVariantChanged);
      // Invalidates any lookup still in flight.
      this.requestId += 1;
    }

    get pincode() {
      return this.input.value;
    }

    get isValid() {
      return PINCODE_PATTERN.test(this.pincode);
    }

    // Everything the API refuses to answer without. SKU is not here: GoKwik
    // answers an empty SKU the same as a real one.
    get isConfigured() {
      return Boolean(this.credentials.token && this.credentials.merchantId);
    }

    // The single "may a lookup start" test, shared by the button, Enter and
    // the chips. A failed lookup can be retried; an answered one is CHANGEd.
    get canCheck() {
      return this.isConfigured && this.isValid && (this.status === 'idle' || this.status === 'failed');
    }

    handleInput() {
      // Digits only: a paste of "560 102" or "560-102" still lands as six.
      const digits = this.pincode.replace(/\D/g, '').slice(0, 6);
      if (digits !== this.pincode) this.input.value = digits;

      // Any edit means whatever is on screen — answer, failure or a lookup in
      // flight — is no longer for this input, and no chip owns it any more.
      if (this.status !== 'idle') this.reset();
      this.syncButton();
    }

    // Implicit submission: Enter in the only text input of a form fires the
    // form's submit, which here is add-to-cart. Suppress it and check instead.
    handleKeydown(event) {
      if (event.key !== 'Enter') return;

      event.preventDefault();
      if (this.canCheck) this.check();
    }

    handleClick() {
      // CHANGE: clear for a new pincode rather than re-check the same one.
      if (this.status === 'answered') {
        this.input.value = '';
        this.reset();
        this.input.focus();
        return;
      }

      if (this.canCheck) this.check();
    }

    // Delegated so one listener serves every chip.
    handleCityClick(event) {
      const chip = event.target.closest('[data-hira-city-pincode]');
      if (!chip || !this.isConfigured) return;

      const { hiraCityPincode: pincode } = chip.dataset;
      // Already showing, or already fetching, this city. A failure retries.
      if (pincode === this.cityPincode && (this.status === 'answered' || this.status === 'loading')) return;

      this.input.value = pincode;
      this.reset();
      this.setCity(pincode);
      this.check();
    }

    handleVariantChanged(event) {
      const { variant } = event.detail ?? {};
      if (!variant) return;

      const skuChanged = (variant.sku ?? '') !== this.variant.sku;
      this.variant = { sku: variant.sku ?? '', price: Number(variant.price) || 0 };

      // The estimate is per SKU. Re-running also supersedes a lookup still in
      // flight for the previous size, whose answer would otherwise land as current.
      if (skuChanged && (this.status === 'answered' || this.status === 'loading')) this.check();
    }

    async check() {
      const pincode = this.pincode;
      this.requestId += 1;
      const { requestId } = this;

      this.setStatus('loading');

      let result = null;
      try {
        result = await requestEdd({ ...this.credentials, ...this.variant, pincode });
      } catch (error) {
        console.warn('[hira-pincode] delivery estimate lookup failed', error);
      }

      // A newer lookup, an edit, or a re-render has superseded this one.
      if (requestId !== this.requestId) return;

      if (result) {
        if (this.cityPincode === null) writeStored(PINCODE_KEY, pincode);
        this.setStatus('answered', { tone: result.available ? 'is-available' : 'is-unavailable', text: result.text });
      } else {
        this.setStatus('failed', { tone: 'is-unavailable', text: this.failedText });
      }
    }

    // The only writer of `status`, so the note and the button always move with it.
    setStatus(status, { tone = null, text = null } = {}) {
      this.status = status;

      this.note.classList.remove(...STATES);
      if (status === 'loading') this.note.classList.add('is-loading');
      if (tone) this.note.classList.add(tone);

      if (text !== null) {
        this.noteText.textContent = text;
        this.note.hidden = false;
      }

      this.syncButton();
    }

    setCity(pincode) {
      this.cityPincode = pincode;
      for (const chip of this.cities) {
        chip.setAttribute('aria-pressed', String(chip.dataset.hiraCityPincode === pincode));
      }
    }

    // Back to the merchant's idle Delivery Note, with no chip selected.
    reset() {
      this.requestId += 1;
      this.setCity(null);
      this.noteText.replaceChildren(...this.idleNodes.map((node) => node.cloneNode(true)));
      this.note.hidden = this.idleHidden;
      this.setStatus('idle');
    }

    syncButton() {
      const { check, change, checking } = this.labels;
      const labels = { loading: checking, answered: change };

      this.button.textContent = labels[this.status] ?? check;
      this.button.disabled = this.status === 'loading' || (this.status !== 'answered' && !this.canCheck);
    }
  }

  if (!customElements.get('hira-pincode')) {
    customElements.define('hira-pincode', HiraPincode);
  }

  // Coupon code copy button. Delegated on `document` rather than scoped to
  // `hira-pincode` — the coupon cards are a sibling of that element, not a
  // descendant, and delegation survives `shopify:section:load` re-renders
  // without a rebind.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-hira-coupon-copy]');
    if (!button) return;

    const code = button.dataset.code;
    if (!code) return;

    navigator.clipboard
      .writeText(code)
      .then(() => {
        button.classList.add('is-copied');
        setTimeout(() => {
          button.classList.remove('is-copied');
        }, 2000);
      })
      .catch((error) => {
        console.error('Failed to copy coupon code:', error);
      });
  });

  // The offer row hides its scrollbar (`.hira-hero__offers`), which leaves
  // desktop mouse users with no drag handle and no way back to earlier cards
  // — a trackpad/touch swipe can scroll it, but a plain wheel cannot. This
  // redirects normal vertical wheel input into horizontal scroll whenever
  // the pointer is over a row that actually overflows, without touching
  // trackpads already sending horizontal deltas or rows that fit and don't
  // need it.
  document.addEventListener(
    'wheel',
    (event) => {
      const row = event.target.closest('.hira-hero__offers');
      if (!row) return;
      if (row.scrollWidth <= row.clientWidth) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      event.preventDefault();
      row.scrollLeft += event.deltaY;
    },
    { passive: false },
  );
})();
